import * as React from 'react'
import { clsx } from 'clsx'

import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  type Row as RowType,
  ColumnDef,
} from '@tanstack/react-table'

import {
  isInstanceBlock,
  type ComponentBlock,
  type InstanceBlock,
  type ListBlock,
} from '@basehub/data/replicache/index'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/sotano-kit/primitives/table'

import { useListTable } from './use-list-table'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useAntiLayoutShift } from '~/hooks/use-anti-layout-shift'

export const ListBlockLight: React.FC<{
  block: ListBlock
}> = ({ block }) => {
  const treeManager = useWorkingTreeStore((s) => s.tree)
  const { columns, rows } = useListTable({
    block,
    treeManager,
  })
  const { ref, antiLayoutShiftStyle } = useAntiLayoutShift<HTMLDivElement>({
    blockId: block.id,
    storeSuffix: 'list',
    initialValue: 70,
  })

  return (
    <div
      ref={ref}
      style={antiLayoutShiftStyle}
      className="relative min-w-max rounded-lg border border-dark-border-solid overflow-hidden"
    >
      <DataTable columns={columns} data={rows} />
    </div>
  )
}

// Light table

declare module '@tanstack/react-table' {
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  interface ColumnMeta<TData, TValue> {
    sticky?: boolean
  }
}

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  onClickRow?: (row: RowType<TData>) => void
}

export function DataTable<
  TData extends InstanceBlock | ComponentBlock,
  TValue,
>({ columns, data }: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: { pageSize: 2500, pageIndex: 0 },
    },
  })

  return (
    <Table className="text-[13px] leading-4">
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSticky = header.column.columnDef.meta?.sticky

              return (
                <TableHead
                  key={header.id}
                  className={clsx(
                    isSticky && 'sticky right-0 border-l bg-dark-shade',
                    'overflow-hidden text-ellipsis whitespace-nowrap border-l border-b border-dark-border-solid font-normal first:border-l-0 last:border-r-0'
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              )
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="text-dark-muted">
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row, index) => {
            const block = row.original

            return (
              <Row key={block.id} block={block}>
                {row.getVisibleCells().map((cell) => {
                  return (
                    <TableCell
                      key={cell.id}
                      className={clsx(
                        index < table.getRowModel().rows.length - 1 &&
                          'border-b',
                        'overflow-hidden text-ellipsis whitespace-nowrap border-l border-dark-border-solid first:border-l-0 last:border-r-0'
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  )
                })}
              </Row>
            )
          })
        ) : (
          <TableRow>
            <TableCell className="pl-3 bg-dark-base" colSpan={columns.length}>
              No records.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

type RowProps = {
  block: InstanceBlock | ComponentBlock
  index: number
  className?: string
  onClick?: () => void
}

const Row = React.forwardRef<
  HTMLTableRowElement,
  React.PropsWithChildren<Omit<RowProps, 'index'>>
>(({ block, className, onClick, children }, ref) => (
  <TableRow
    ref={ref}
    onClick={onClick}
    className={clsx(
      'relative bg-dark-base',
      onClick && 'hover:bg-dark-shade',
      isInstanceBlock(block) && block.hidden && '[&_td]:opacity-40',
      className
    )}
  >
    {children}
  </TableRow>
))
